export type AuditDiffProps = { before: unknown; after: unknown };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const nameOf = (record: Record<string, unknown>): string =>
  String(record.name ?? record.hostname ?? record.path ?? record.url ?? '');

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

/**
 * Renders a change as a readable diff.
 *
 * Destination-set saves are summarised by count rather than dumped as JSON:
 * "1 destination → 2 destinations" answers the question an operator scanning the
 * log actually has, and the full detail is one click away in the entry itself.
 */
export function AuditDiff({ before, after }: AuditDiffProps) {
  if (Array.isArray(before) || Array.isArray(after)) {
    const from = Array.isArray(before) ? before.length : 0;
    const to = Array.isArray(after) ? after.length : 0;
    return (
      <p className="text-[length:var(--text-small)] text-ink-muted">
        {plural(from, 'destination')} → {plural(to, 'destination')}
      </p>
    );
  }

  if (before === null && isRecord(after)) {
    return (
      <p className="text-[length:var(--text-small)] text-ink-muted">
        Created <span className="font-mono">{nameOf(after)}</span>
      </p>
    );
  }

  if (after === null && isRecord(before)) {
    return (
      <p className="text-[length:var(--text-small)] text-ink-muted">
        Deleted <span className="font-mono">{nameOf(before)}</span>
      </p>
    );
  }

  if (!isRecord(before) || !isRecord(after)) {
    return <p className="text-[length:var(--text-small)] text-ink-subtle">No detail recorded</p>;
  }

  const changed = Object.keys({ ...before, ...after }).filter(
    (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]),
  );

  if (changed.length === 0) {
    return <p className="text-[length:var(--text-small)] text-ink-subtle">No field changed</p>;
  }

  return (
    <ul className="flex flex-col gap-(--space-1) text-[length:var(--text-small)]">
      {changed.map((key) => (
        <li key={key} className="flex flex-wrap items-baseline gap-(--space-2)">
          <span className="text-ink-subtle">{key}</span>
          <span className="font-mono text-ink-muted line-through">{String(before[key] ?? '—')}</span>
          <span aria-hidden="true" className="text-ink-subtle">
            →
          </span>
          <span className="font-mono text-ink">{String(after[key] ?? '—')}</span>
        </li>
      ))}
    </ul>
  );
}
